import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { useCan } from '@refinedev/core';
import { Table, Tag, Space, Button, Modal, message } from 'antd';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';

const actionColors: Record<string, string> = {
  CREATE: '#52c41a',
  UPDATE: '#1890ff',
  LABEL_OCR_PROMOTE: '#722ed1',
  LABEL_OCR_PERSONAL: '#13c2c2',
  REVIEWED: '#fa8c16',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const TaggingLogList: React.FC = () => {
  const { tableProps, tableQueryResult } = useTable({
    resource: 'tagging-logs',
    pagination: { current: 1, pageSize: 20 },
    sorters: { initial: [{ field: 'createdAt', order: 'desc' }] },
  });

  const { data: canDelete } = useCan({ resource: 'tagging-logs', action: 'delete' });
  const [enlargeImages, setEnlargeImages] = useState<{ scan?: string; label?: string } | null>(null);

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete this log?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosInstance.delete(`${API_BASE_URL}/dish-catalog/tagging-logs/${id}`);
          message.success('Log deleted');
          tableQueryResult.refetch();
        } catch {
          message.error('Failed to delete log');
        }
      },
    });
  };

  return (
    <List resource="tagging-logs">
      <Table {...tableProps} rowKey="id" scroll={{ x: 1100 }}>
        <Table.Column
          dataIndex="action"
          title="Action"
          width={100}
          filters={[
            { text: 'Create', value: 'CREATE' },
            { text: 'Update', value: 'UPDATE' },
            { text: 'Label OCR promote', value: 'LABEL_OCR_PROMOTE' },
            { text: 'Label OCR personal', value: 'LABEL_OCR_PERSONAL' },
            { text: 'Reviewed', value: 'REVIEWED' },
          ]}
          onFilter={(value, record: any) => record.action === value}
          render={(action: string) => (
            <Tag style={tagStyle(actionColors[action] || '#898989')}>{action}</Tag>
          )}
        />
        <Table.Column
          title="Images"
          width={140}
          render={(_: unknown, record: any) => {
            const scan = record.scanImageUrl;
            const label = record.labelImageUrl;
            if (!scan && !label) return <span style={{ color: '#bfbfbf' }}>—</span>;
            return (
              <Space size={4} onClick={() => setEnlargeImages({ scan, label })} style={{ cursor: 'pointer' }}>
                {scan && (
                  <img
                    src={scan}
                    alt="scan"
                    title="Scan"
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, background: '#fafafa' }}
                  />
                )}
                {label && (
                  <img
                    src={label}
                    alt="label"
                    title="Label"
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, background: '#fafafa' }}
                  />
                )}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="dishCatalogId"
          title="Dish Catalog ID"
          ellipsis
          width={200}
          render={(id: string | null) => id ? (
            <Link to={`/dish-catalog/show/${id}`} style={{ fontSize: 12, fontFamily: 'monospace' }}>
              {id.slice(0, 8)}...
            </Link>
          ) : '—'}
        />
        <Table.Column
          dataIndex="userName"
          title="Trainer"
          width={140}
          render={(name: string) => name || '—'}
        />
        <Table.Column
          dataIndex="changedFields"
          title="Changed Fields"
          render={(fields: Record<string, { before: unknown; after: unknown }> | null) => {
            if (!fields) return '—';
            const keys = Object.keys(fields);
            return (
              <Space size={4} wrap>
                {keys.map((k) => <Tag key={k}>{k}</Tag>)}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          width={180}
          render={(date: string) => date ? new Date(date).toLocaleString() : '—'}
        />
        <Table.Column
          title="Actions"
          width={canDelete?.can ? 120 : 80}
          render={(_: unknown, record: any) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="tagging-logs" />
              {canDelete?.can && (
                <Button type="link" danger size="small" onClick={() => handleDelete(record.id)}>Delete</Button>
              )}
            </Space>
          )}
        />
      </Table>
      <Modal
        open={!!enlargeImages}
        title="Tagging Log Images"
        footer={null}
        onCancel={() => setEnlargeImages(null)}
        width={760}
      >
        <Space size={12} wrap align="start">
          {enlargeImages?.scan && (
            <div>
              <div style={{ fontSize: 12, color: '#898989', marginBottom: 4 }}>Scan</div>
              <img
                src={enlargeImages.scan}
                alt="scan"
                style={{ maxWidth: 340, maxHeight: 340, objectFit: 'contain', borderRadius: 4, background: '#fafafa' }}
              />
            </div>
          )}
          {enlargeImages?.label && (
            <div>
              <div style={{ fontSize: 12, color: '#898989', marginBottom: 4 }}>Label</div>
              <img
                src={enlargeImages.label}
                alt="label"
                style={{ maxWidth: 340, maxHeight: 340, objectFit: 'contain', borderRadius: 4, background: '#fafafa' }}
              />
            </div>
          )}
        </Space>
      </Modal>
    </List>
  );
};
